import { formateDateMonth } from "@/src/shared/utils/timeFunctionsModel";
import { type IGuestOptions } from "@/src/shared/features/search";
import { visibleSmallerSearchBarRoute } from "@/src/shared/constants/app";
import type { ILocationFixedDataType } from "@/src/shared/types/locations";

export function formatBaths(bathCount?: number | null): string {
  if (!Number.isFinite(bathCount as number) || (bathCount as number) <= 0) {
    return "No bath";
  }

  const count = bathCount as number;

  if (count === 1) return "attached bath";
  if (count < 1) return "non-attached bath";

  const wholeBaths = Math.trunc(count);
  return `${wholeBaths} bath${wholeBaths > 1 ? "s" : ""}`;
}

export const getFormattedDateRange = (
  checkIn?: Date | string | null,
  checkOut?: Date | string | null,
): string => {
  if (checkIn && checkOut) {
    return `${formateDateMonth(checkIn)} - ${formateDateMonth(checkOut)}`;
  }
  return "";
};

// Compact intl-based date formatters used by hotel pages. Kept here so any
// page can render dates with a single shared style.
export const formatShortDate = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
  locale: string = "en-GB",
): string => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale, options);
};

export const formatShortDateRange = (
  from: Date | string | null | undefined,
  to: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  separator: string = " – ",
  locale: string = "en-GB",
): string => {
  const start = formatShortDate(from, options, locale);
  const end = formatShortDate(to, options, locale);
  if (!start || !end) return start || end;
  return `${start}${separator}${end}`;
};

/**
 * "1 Room" / "2 Rooms" — handle the common "noun + s" plural with one call.
 * `pluralSuffix` lets you handle irregular plurals like "Child" → "Children"
 * by passing the full replacement suffix (e.g. `pluralize(3, "Child", "ren")`).
 */
export function pluralize(
  count: number,
  singular: string,
  pluralSuffix: string = "s",
): string {
  const word = count === 1 ? singular : `${singular}${pluralSuffix}`;
  return `${count} ${word}`;
}

interface IGuestBreakdownOptions {
  capitalize?: boolean; // "Adult" vs "adult"
  includeInfants?: boolean; // include "1 Infant" when infants > 0
  includeZero?: boolean; // include "0 Adults" instead of skipping
  separator?: string; // default ", "
}

/**
 * Format an adult/child/infant breakdown like "2 Adults, 1 Child, 1 Infant".
 *
 * Replaces the repeated `${n} Adult${n !== 1 ? "s" : ""}` patterns scattered
 * across the hotel pages. Children pluralizes irregularly (Child → Children).
 */
export function formatGuestBreakdown(
  guests: { adults?: number; children?: number; infants?: number },
  options: IGuestBreakdownOptions = {},
): string {
  const {
    capitalize = true,
    includeInfants = true,
    includeZero = false,
    separator = ", ",
  } = options;
  const adults = guests.adults ?? 0;
  const children = guests.children ?? 0;
  const infants = guests.infants ?? 0;

  const adultLabel = capitalize ? "Adult" : "adult";
  const childLabel = capitalize ? "Child" : "child";
  const infantLabel = capitalize ? "Infant" : "infant";

  const parts: string[] = [];
  if (adults > 0 || includeZero) parts.push(pluralize(adults, adultLabel));
  if (children > 0 || includeZero)
    parts.push(pluralize(children, childLabel, "ren"));
  if (includeInfants && (infants > 0 || includeZero)) {
    parts.push(pluralize(infants, infantLabel));
  }
  return parts.join(separator);
}

export function formatGuestText(options: IGuestOptions): string {
  const totalGuests = (options.adults || 0) + (options.children || 0);
  const infants = options.infants || 0;

  const parts: string[] = [];

  if (totalGuests > 0) {
    parts.push(`${totalGuests} guest${totalGuests !== 1 ? "s" : ""}`);
  }

  if (infants > 0) {
    parts.push(`${infants} infant${infants !== 1 ? "s" : ""}`);
  }

  return parts.length > 0 ? parts.join(", ") : "";
}

export const isAppliedSmallerSearchBar = (pathname: string) => {
  return visibleSmallerSearchBarRoute.some((p) => !!pathname?.startsWith(p));
};

// Fuzzy string matching using Levenshtein distance
function levenshtein(a: string, b: string): number {
  // Two rows of the edit-distance table are enough: row i only reads row i-1.
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        (prev[j] ?? 0) + 1,
        (curr[j - 1] ?? 0) + 1,
        (prev[j - 1] ?? 0) + cost,
      );
    }
    prev = curr;
  }
  return prev[b.length] ?? 0;
}

export function normalize(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function smartFindLocation(
  name: string,
  locations: Array<ILocationFixedDataType>,
) {
  if (!name) return locations[0];
  const normalizedInput = normalize(name);
  let bestMatch = locations[0];
  let bestScore = Infinity;
  for (const loc of locations) {
    const locNorm = normalize(loc.name);
    const score = levenshtein(normalizedInput, locNorm);
    if (score < bestScore) {
      bestScore = score;
      bestMatch = loc;
    }
  }
  return bestMatch;
}

/**
 * Random id for a single analytics event (used server-side for de-duplication).
 * Falls back to a random + timestamp string where `crypto.randomUUID` is missing
 * (older Safari, non-secure contexts).
 */
export function generateEventId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Math.random().toString(36).substring(2, 15)}${Math.random()
    .toString(36)
    .substring(2, 15)}-${Date.now().toString(36)}`;
}
